import {
  Button,
  Dropdown,
  Label,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Tab,
  TabList,
  TabValue,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { AppDispatch, RootState } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProviders,
  setAppliedSelectedDates,
  setSelectedProviders,
} from "../../../redux/Commission/commissionSlice";
import { dateOptions } from "../../../shared/commissionDateOptions";
import SearchableDropdown from "../../../components/SearcbaleDropdown/SearchableDropdown";
import DynamicDatePicker from "../../../components/ControlledDatePicker/dynamicDatepicker";

const useStyles = makeStyles({
  menuPopover: {
    minWidth: "500px",
    ...shorthands.padding("12px"),
  },
  tabPanel: {
    ...shorthands.padding("15px"),
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  datePickers: {
    display: "flex",
    ...shorthands.gap("8px"),
    marginTop: "8px",
  },
  applyButton: {
    marginTop: "16px",
    width: "100%",
  },
  badge: {
    marginLeft: "4px",
    verticalAlign: "text-top",
  },
  filterButton: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  listbox: {
    maxHeight: "125px",
    overflowY: "auto",
    ...shorthands.margin("0"), // optional
  },
});

interface FilterTabProps {
  title: string;
  providerOptions: { label: string; id: string; checked: boolean }[];
  selectedMenuItem: string;
  getReceiptlistData: () => void;
  filterPopup: boolean;
  setFilterPopup: (open: boolean) => void;
  dateOption?: any;
  setDateOption?: any;
}

const FilterTab = (props: FilterTabProps) => {
  const styles = useStyles();
  const dispatch: AppDispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState<TabValue>("date");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedProviderIds, setLocalSelectedProviderIds] = useState<
    string[]
  >([]);
  const dropdownOptions = useMemo(() => [
    { label: "", value: "00000000-0000-0000-0000-000000000000" },
    ...props.providerOptions.map((provider) => ({
      label: provider.label,
      value: provider.id,
    })),
  ], [props.providerOptions]);
  const providers = useSelector(
    (state: RootState) => state.commission.providers
  );
  const selectedProviders = useSelector(
    (state: RootState) => state.commission.selectedProviders
  );
  const selectedProviderIds = selectedProviders.map((provider) => provider.id);

  const appliedSelectedDates = useSelector(
    (state: RootState) => state.commission.appliedSelectedDates
  );

  const getDateFilter = (
    dateOption: string,
    startDate?: Date | null,
    endDate?: Date | null
  ) => {
    const today = moment().endOf("day");
    let start: moment.Moment | null = null;
    let end: moment.Moment | null = today.clone();

    switch (props?.dateOption) {
      case "lastWeek":
        start = today.clone().subtract(1, "week").startOf("week");
        end = today.clone().subtract(1, "week").endOf("week");
        break;
      case "thisMonth":
        start = today.clone().startOf("month");
        break;
      case "last3Months":
        start = today.clone().subtract(3, "months").startOf("month");
        break;
      case "last6Months":
        start = today.clone().subtract(6, "months").startOf("month");
        break;
      case "last12Months":
        start = today.clone().subtract(12, "months").startOf("month");
        break;
      case "lastCalendarYear":
        start = today.clone().subtract(1, "year").startOf("year");
        end = today.clone().subtract(1, "year").endOf("year");
        break;
      case "includeAllDates":
        return "includeAllDates";
      case "custom":
        start = startDate ? moment(startDate) : null;
        end = endDate ? moment(endDate) : null;
        break;
      default:
        return props?.dateOption;
    }

    if (start && end) {
      // Matches your required payload format
      return `custom&start_date=${start.format("YYYY-MM-DD")}&end_date=${end.format("YYYY-MM-DD")}`;
    }
    return props?.dateOption;
  };

  // FIX: Parsing logic to retain values in the UI
  useEffect(() => {
    if (isOpen && appliedSelectedDates) {
      // Use URLSearchParams to parse your custom string
      // We remove 'custom&' from the start so URLSearchParams can read the rest as key-value pairs
      const queryString = appliedSelectedDates.replace("custom&", "");
      const params = new URLSearchParams(queryString);

      // Changed from fromDate/toDate to start_date/end_date to match your return format
      const start = params.get("start_date");
      const end = params.get("end_date");

      if (start) {
        setStartDate(moment(start).toDate());
      }
      if (end) {
        setEndDate(moment(end).toDate());
      }
    }
  }, [isOpen, appliedSelectedDates]);

  const handleApply = () => {
    const dateFilter = getDateFilter(props?.dateOption, startDate, endDate);
    dispatch(setAppliedSelectedDates(dateFilter));

    const newProviders = dropdownOptions
      .filter((provider) => localSelectedProviderIds.includes(provider.value))
      .map((provider) => ({
        id: provider.value,
        name: provider.label,
      }));

    dispatch(setSelectedProviders(newProviders));
    props.getReceiptlistData();
    setIsOpen(false);
  };

  // // Initialize from props and sync Provider IDs
  // useEffect(() => {
  //   setIsOpen(props.filterPopup);
  //   if (props.filterPopup) {
  //     setLocalSelectedProviderIds(selectedProviderIds);
  //   }
  // }, [props.filterPopup, selectedProviderIds]);

  // // Sync with parent state
  // useEffect(() => {
  //   props.setFilterPopup(isOpen);
  // }, [isOpen]);

  // Initialize from props
  useEffect(() => {
    setIsOpen(props.filterPopup);
    if (props.filterPopup) {
      setLocalSelectedProviderIds(selectedProviderIds);
    }
  }, [props.filterPopup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync with parent state
  useEffect(() => {
    props.setFilterPopup(isOpen);
  }, [isOpen]);
  useEffect(() => {
    if (selectedTab === "provider" && providers.length < 2968) {
      dispatch(fetchProviders({ page: 1, limit: 3000, fetchAll: true }));
    }
  }, [selectedTab, providers.length, dispatch]);
  const onFormatDate = (date?: Date): string => {
    return !date ? "" : moment(date).format("DD-MM-YYYY");
  };

  const getButtonContent = () => {
    return (
      <div className={styles.filterButton}>
        <span>{props.selectedMenuItem}</span>
      </div>
    );
  };

  return (
    <Menu
      open={isOpen}
      onOpenChange={(_, { open }) => {
        setIsOpen(open);
        if (!open) {
          setLocalSelectedProviderIds(selectedProviderIds);
        }
      }}
      positioning="below-end"
    >
      <MenuTrigger>
        <MenuButton>{getButtonContent()}</MenuButton>
      </MenuTrigger>

      <MenuPopover className={styles.menuPopover}>
        <MenuList>
          <MenuGroup>
            <TabList
              selectedValue={selectedTab}
              onTabSelect={(_, { value }) => setSelectedTab(value)}
            >
              <Tab value="date">Select Date</Tab>
              <Tab value="provider" data-testid="select_provider_tab">
                Select Provider
              </Tab>
            </TabList>

            {selectedTab === "date" && (
              <div className={styles.tabPanel}>
                <Label>Select Date Range</Label>
                <Dropdown
                  placeholder={
                    props?.dateOption
                      ? dateOptions.find(
                        (opt) => opt.value === props.dateOption
                      )?.label
                      : "Select Date Range"
                  }
                  selectedOptions={[props?.dateOption]}
                  onOptionSelect={(_, data) =>
                    props?.setDateOption(data.optionValue as string)
                  }
                  style={{ marginBottom: "12px" }}
                >
                  {dateOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Dropdown>

                {props?.dateOption === "custom" && (
                  <div className={styles.datePickers}>
                    <div style={{ flex: 1 }}>
                      <DynamicDatePicker
                        border
                        name="start_date"
                        label="Start Date"
                        required={false}
                        disabled={false}
                        formik={{
                          values: {
                            start_date: startDate ?? null,
                          },
                          setFieldValue: (
                            _name: string,
                            value: Date | null
                          ) => {
                            setStartDate(value);
                          },
                          setFieldTouched: () => { },
                          setFieldError: () => { },
                          errors: {},
                          touched: {},
                        }}
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <DynamicDatePicker
                        border
                        name="end_date"
                        label="End Date"
                        required={false}
                        disabled={false}
                        formik={{
                          values: {
                            end_date: endDate ?? null,
                          },
                          setFieldValue: (
                            _name: string,
                            value: Date | null
                          ) => {
                            setEndDate(value);
                          },
                          setFieldTouched: () => { },
                          setFieldError: () => { },
                          errors: {},
                          touched: {},
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedTab === "provider" && (
              <div className={styles.tabPanel}>
                <span style={{ color: "red" }}>
                  {localSelectedProviderIds.length > 10 &&
                    "Max limit of allowed providers is 10"}
                </span>
                <label>Select Providers</label>
                <SearchableDropdown
                  select_all={false}
                  fixed_width={false}
                  style={styles.listbox}
                  options={dropdownOptions}
                  selectedDropDownValues={localSelectedProviderIds}
                  setSelectedDropDownValues={setLocalSelectedProviderIds}
                  placeholder="Select Providers"
                  maxSelectionLimit={10}
                />
              </div>
            )}
            <div className="flex-end">
              <Button
                onClick={() => {
                  props.setFilterPopup(false);
                  setLocalSelectedProviderIds(selectedProviderIds);
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                className="asc-button-primary"
                onClick={handleApply}
                disabled={
                  props?.dateOption === "custom" && (!startDate || !endDate)
                }
              >
                Apply Filters
              </Button>
            </div>
          </MenuGroup>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default FilterTab;
